import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Menu,
} from '@mui/material'
import IndividualCodeDetailList from './IndividualCodeDetailList'
import {
  CodeDetail,
  CodeValidationDiscipline,
  CodeValidationStatus,
} from '../../models/Validation'

export const IndividualTabContent = React.memo(({ codeGroups }: any) => {
  const [codeType, setCodeType] = useState<CodeValidationDiscipline>(
    CodeValidationDiscipline.Architectural,
  )
  const [codeStatus, setCodeStatus] = useState<CodeValidationStatus | 'ALL'>(
    'ALL',
  )
  const [codeCategory, setCodeCategory] = useState<String>('measurement')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null)

  const CODE_BATCH_SIZE = 20
  const [visibleCodeCount, setVisibleCodeCount] = useState(CODE_BATCH_SIZE)

  const allCodes: CodeDetail[] = useMemo(
    () => codeGroups.flatMap((group: any) => group.codes),
    [codeGroups],
  )
  const filteredCodes = useMemo(() => {
    let codes = allCodes.filter((c: CodeDetail) => c.discipline === codeType)
    if (codeStatus && codeStatus !== 'ALL') {
      codes = codes.filter((code: CodeDetail) => {
        if (!code.elements || code.elements.length === 0) {
          return codeStatus === CodeValidationStatus.Failed
        }
        return code.elements.every((el: any) => el.status === codeStatus)
      })
    }
    if (codeCategory) {
      codes.map((code: CodeDetail) => {
        if (!code.atomics) {
          console.log(`Code ${code.codeReference} has no atomics defined.`)
          return code
        }
        console.log(`Code ${code.codeReference} has atomics:`, code.atomics)
        return code
      })
      codes = codes.filter(
        (code: CodeDetail) =>
          code.atomics &&
          code.atomics.some((atomic) => atomic.codeCategory === codeCategory),
      )
    }
    codes = codes.slice().sort((a: CodeDetail, b: CodeDetail) => {
      const aDesc = a.description || ''
      const bDesc = b.description || ''
      if (sortOrder === 'asc') {
        return aDesc.localeCompare(bDesc)
      } else {
        return bDesc.localeCompare(aDesc)
      }
    })
    return codes
  }, [allCodes, codeType, codeStatus, codeCategory, sortOrder])

  useEffect(() => {
    setVisibleCodeCount(CODE_BATCH_SIZE)
  }, [codeType, codeStatus, codeCategory, sortOrder])

  const handleCodeListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setVisibleCodeCount((prev) =>
        Math.min(prev + CODE_BATCH_SIZE, filteredCodes.length),
      )
    }
  }

  const visibleCodes = useMemo(() => {
    return filteredCodes.slice(0, visibleCodeCount)
  }, [filteredCodes, visibleCodeCount])

  return (
    <Box sx={{ height: '100%' }}>
      <Box
        display='flex'
        justifyContent='flex-start'
        mb={1}
        gap={1}
        alignItems='center'
      >
        <FormControl size='small' sx={{ minWidth: 120 }}>
          <Select
            value={codeType}
            onChange={(e) =>
              setCodeType(e.target.value as CodeValidationDiscipline)
            }
            displayEmpty
            sx={{ bgcolor: '#E4E4E4', height: '31px', fontSize: '11px' }}
          >
            <MenuItem value={CodeValidationDiscipline.Architectural}>
              Architectural
            </MenuItem>
            <MenuItem value={CodeValidationDiscipline.Other}>
              Electrical
            </MenuItem>
            <MenuItem value={CodeValidationDiscipline.Structural}>
              Structural
            </MenuItem>
            <MenuItem value={CodeValidationDiscipline.MEP}>Mechanical</MenuItem>
          </Select>
        </FormControl>
        <FormControl size='small' sx={{ minWidth: 120 }}>
          <Select
            value={codeCategory}
            onChange={(e) =>
              setCodeCategory(e.target.value as CodeValidationDiscipline)
            }
            displayEmpty
            sx={{
              bgcolor: '#E4E4E4',
              height: '31px',
              fontSize: '11px',
              '& .Mui-selected': {
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            <MenuItem value={'measurement'}>Measurement</MenuItem>
            <MenuItem value={'inspection'}>Inspection</MenuItem>
          </Select>
        </FormControl>
        <FormControl size='small' sx={{ minWidth: 100 }}>
          <Select
            value={codeStatus}
            onChange={(e) =>
              setCodeStatus(e.target.value as CodeValidationStatus | 'ALL')
            }
            displayEmpty
            sx={{ bgcolor: '#E4E4E4', height: '31px', fontSize: '11px' }}
          >
            <MenuItem value='ALL'>ALL</MenuItem>
            <MenuItem value={CodeValidationStatus.Passed}>
              {CodeValidationStatus.Passed}
            </MenuItem>
            <MenuItem value={CodeValidationStatus.Failed}>
              {CodeValidationStatus.Failed}
            </MenuItem>
          </Select>
        </FormControl>
        <Box flexGrow={1} />
        <IconButton
          aria-label='sort'
          onClick={(e) => setSortAnchorEl(e.currentTarget)}
          size='small'
          sx={{ bgcolor: '#fff', border: '1px solid #E0E0E0', borderRadius: 1 }}
        >
          <svg
            width='20'
            height='15'
            viewBox='0 0 20 15'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M4 7.5H16M1 1.5H19M7 13.5H13'
              stroke='#04002E'
              strokeWidth='1.4'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </IconButton>
      </Box>
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={() => setSortAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem
          selected={sortOrder === 'asc'}
          onClick={() => {
            setSortOrder('asc')
            setSortAnchorEl(null)
          }}
        >
          A-Z
        </MenuItem>
        <MenuItem
          selected={sortOrder === 'desc'}
          onClick={() => {
            setSortOrder('desc')
            setSortAnchorEl(null)
          }}
        >
          Z-A
        </MenuItem>
      </Menu>
      <Box
        sx={{ height: 'calc(100% - 48px)', overflowY: 'auto' }}
        onScroll={handleCodeListScroll}
      >
        <IndividualCodeDetailList codes={visibleCodes} />
      </Box>
    </Box>
  )
})
